import { type TutorialStep, STEPS } from "../constants/tutorial-steps";

interface TutorialModalStepProps {
  readonly current: TutorialStep;
  readonly step: number;
  readonly onPrev: () => void;
  readonly onNext: () => void;
  readonly onSkip: () => void;
}

function StepDots() {
  return (
    <div className="flex items-center gap-1.5">
      {STEPS.map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all ${
            i === STEPS.findIndex((s) => s === STEPS[i])
              ? "bg-accent w-4"
              : i < STEPS.findIndex((s) => s === STEPS[i])
                ? "bg-accent/40 w-1.5"
                : "bg-ink-faint/30 w-1.5"
          }`}
        />
      ))}
    </div>
  );
}

function StepActions({
  step,
  isLast,
  onPrev,
  onNext,
  onSkip,
}: {
  step: number;
  isLast: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="flex items-center justify-between w-full gap-3">
      <button
        type="button"
        onClick={onSkip}
        className="text-[11px] text-ink-muted hover:text-ink transition-colors"
      >
        Passer
      </button>
      <div className="flex items-center gap-2">
        {step > 0 && (
          <button
            type="button"
            onClick={onPrev}
            className="px-3 py-2 text-xs font-medium text-ink-muted hover:text-ink rounded-md hover:bg-surface-alt transition-colors"
          >
            Précédent
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          className="px-3 py-2 text-xs font-medium text-white bg-accent rounded-md hover:opacity-90 transition-opacity"
        >
          {isLast ? "Terminer" : "Suivant"}
        </button>
      </div>
    </div>
  );
}

export function TutorialModalStep({
  current,
  step,
  onPrev,
  onNext,
  onSkip,
}: TutorialModalStepProps) {
  const isLast = step === STEPS.length - 1;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60] animate-fade-in" />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-surface rounded-2xl shadow-xl w-full max-w-sm p-6 flex flex-col items-center gap-5 animate-scale-in">
          <span className="text-4xl">{current.icon}</span>
          <div className="text-center flex flex-col gap-2">
            <h2 className="text-base font-semibold text-ink">
              {current.title}
            </h2>
            <p className="text-xs text-ink-muted leading-relaxed">
              {current.instruction}
            </p>
            {current.action && (
              <p className="text-[11px] text-accent font-medium mt-1">
                {current.action}
              </p>
            )}
          </div>
          <StepDots />
          <StepActions
            step={step}
            isLast={isLast}
            onPrev={onPrev}
            onNext={onNext}
            onSkip={onSkip}
          />
        </div>
      </div>
    </>
  );
}
